import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Typography, Box, CircularProgress, Alert } from '@mui/material';
import { AppLayout } from '@/components/layout/AppLayout';
import { AccountTable } from '@/components/app/account';
import { useAuth } from '@/hooks/useAuth';
import { useAccounts, useFavoriteAccountIds, useToggleFavorite } from '@/lib/query/hooks';

export function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // アカウント一覧を取得（検索キーワードが変更されると自動的に再取得）
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    error: accountsError,
  } = useAccounts(searchTerm || undefined);

  // お気に入りアカウントID一覧を取得
  const {
    data: favoriteAccountIds = [],
    isLoading: favoritesLoading,
    error: favoritesError,
  } = useFavoriteAccountIds();

  // お気に入り追加/削除のMutation
  const toggleFavoriteMutation = useToggleFavorite();

  const handleAccountClick = (accountId: string) => {
    navigate(`/accounts/${accountId}`);
  };

  // 検索機能（検索キーワードを更新するとuseAccountsが自動的に再取得）
  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  // お気に入りトグル機能
  const handleToggleFavorite = async (accountId: string, shouldBeFavorite: boolean) => {
    try {
      await toggleFavoriteMutation.mutateAsync({ accountId, shouldBeFavorite });
    } catch (error) {
      console.error('お気に入り操作に失敗:', error);
      // エラーハンドリング: 必要に応じてユーザーに通知
    }
  };

  // ローディング状態（アカウントまたはお気に入りのローディング中）
  const loading = accountsLoading || favoritesLoading;
  // エラー状態（アカウントまたはお気に入りのエラー）
  const error = accountsError || favoritesError;

  // 認証ローディング中
  if (authLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }

  // 未認証の場合（ミドルウェアでリダイレクトされるため、ここには到達しないはず）
  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  return (
    <>
      {loading ? (
        <AppLayout showSearch={false}>
          <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
          </Box>
        </AppLayout>
      ) : error ? (
        <AppLayout showSearch={false}>
          <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
            <Alert severity="error">
              {error instanceof Error
                ? error.message
                : 'アカウントデータの読み込みに失敗しました。'}
            </Alert>
          </Box>
        </AppLayout>
      ) : (
        <AppLayout onSearch={handleSearch}>
          <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              アカウント一覧 ({accounts.length}件)
            </Typography>
            <AccountTable
              accounts={accounts}
              onAccountClick={handleAccountClick}
              favoriteAccountIds={favoriteAccountIds}
              showFavoritesOnly={showFavoritesOnly}
              onShowFavoritesOnlyChange={setShowFavoritesOnly}
              onToggleFavorite={handleToggleFavorite}
            />
          </Box>
        </AppLayout>
      )}
    </>
  );
}
